import { memo, useMemo } from 'react';

import { BookmarkIcon, ChevronDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { SearchSubSideNavSavedSearchItem } from '~/components/v2/components/workspace/search-nav/item/SearchSubSideNavSavedSearchItem';
import { SearchSubSideNavSavedSearchItemSkeleton } from '~/components/v2/components/workspace/search-nav/item/SearchSubSideNavSavedSearchItemSkeleton';
import {
  SAVED_SEARCH_LIST_LIMIT,
  SAVED_SEARCH_SKELETON_COUNT,
} from '~/components/v2/components/workspace/search-nav/searchNavItems';
import { SubSideNavItemLabel } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemLabel';
import { SubSideNavItemPrefix } from '~/components/v2/components/workspace/sub-side-nav/item/SubSideNavItemPrefix';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { workspaceSidebarNavigationItem } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarNavigationItem';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { usePrivateSavedSearchList } from '~/components/v2/hooks/queries/usePrivateSavedSearchList';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { canViewSavedFilters } from '~/utils/permissions/workspacePermissions';

export const SearchSubSideNavSavedSearches = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const { data: savedSearchList, isLoading } = usePrivateSavedSearchList(
    {
      workspaceId: currentWorkspace?.id,
      options: { cursor: null, limit: SAVED_SEARCH_LIST_LIMIT, sortBy: 'createdAt' },
    },
    {
      enabled: canViewSavedFilters(permissions),
    },
  );
  const savedSearches = savedSearchList?.data ?? [];

  const savedSearchListContent = useMemo(() => {
    if (isLoading) {
      return Array.from({ length: SAVED_SEARCH_SKELETON_COUNT }, (_, index) => (
        <SearchSubSideNavSavedSearchItemSkeleton key={index} />
      ));
    }

    return (
      <div className="flex flex-col pl-3">
        {savedSearches.map((savedSearch) => (
          <SearchSubSideNavSavedSearchItem key={savedSearch.id} savedSearch={savedSearch} />
        ))}
      </div>
    );
  }, [isLoading, savedSearches]);

  if (!isLoading && savedSearches.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col" data-testid="SEARCH_SUB_SIDE_NAV_SAVED_SEARCHES">
      <button
        aria-expanded={!collapsed}
        className={workspaceSidebarNavigationItem({ appearance: 'subNav', className: 'w-full justify-start' })}
        data-testid="SEARCH_SUB_SIDE_NAV_SAVED_SEARCHES_HEADER"
        onClick={toggleCollapsed}
        type="button"
      >
        <SubSideNavItemPrefix className="text-gray-10">
          <BookmarkIcon color="currentColor" size="sm" />
        </SubSideNavItemPrefix>
        <SubSideNavItemLabel className="flex-none">Saved searches</SubSideNavItemLabel>
        <ChevronDownIcon
          className={cn('shrink-0 text-gray-10 transition-transform', collapsed && '-rotate-90')}
          size="xs"
        />
      </button>
      <WorkspaceSidebarCollapsible open={!collapsed}>{savedSearchListContent}</WorkspaceSidebarCollapsible>
    </section>
  );
});

SearchSubSideNavSavedSearches.displayName = 'SearchSubSideNavSavedSearches';
